import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import queryString from 'query-string';
import _ from 'underscore';
import tinycolor from 'tinycolor2';
import { GetApp, OpenInNew, Share } from '@material-ui/icons';

import { getPreviewLink, joinClassNames } from '../../../util/util';
import { setOpenSharingModal } from '../../../actions/configurationActions';
import ControlButton from '../ControlButton';

import {
	TopControlsAccountTitle,
	TopControlsContainer,
	TopControlsLeft,
	TopControlsLeftFirstRow,
	TopControlsMediaTitle,
	TopControlsPlaybackTitle,
	TopControlsPlaybackTitleWrapper,
	TopControlsProps,
	TopControlsRight,
	TopControlsShadow,
} from './TopControls.styled';
import './TopControls.scss';

const TopControls: React.FC<TopControlsProps> = ({ settings, onMouseEnter, onMouseLeave, children, queryParams = {}, isMobile, className }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const isPlaying = useSelector((state) => (state as any).playerReducer.isPlaying);
	const { mediaName, videoDownloadUrl, playlistId, mediaType, accountId, embedShareSettings, isMediaLive, mediasData, currentMediaId } = useSelector(
		(state) => (state as any).configurationReducer
	);

	const [overShowingTime, setOverShowingTime] = useState<boolean>(false);

	const isShowingTitle = settings?.topControls?.showTitleDuringPlayback
		? settings?.topControls?.showTitleDuringPlayback
		: !overShowingTime
			? true
			: !isPlaying;
	const isShowingAccName = settings?.topControls?.showAccountNameDuringPlayback
		? settings?.topControls?.showAccountNameDuringPlayback
		: !overShowingTime
			? true
			: !isPlaying;

	const currentMedia = useMemo(() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId), [mediasData, currentMediaId]);
	const accountName = useMemo(() => {
		if (!settings.topControls?.accountName) {
			return '';
		}
		if (typeof settings.topControls?.accountName === 'string') {
			return settings.topControls?.accountName;
		}

		const currentLanguage = i18next.languages[0] === 'no' ? 'nb' : i18next.languages[0] === 'dk' ? 'da' : i18next.languages[0];
		return settings.topControls?.accountName[currentLanguage] ?? settings.topControls?.accountName['all'];
	}, [settings.topControls?.accountName]);

	const handleOpenInNewWindow = () => {
		window.open(window.location.href, 'newwindow', 'width=606,height=360');
	};

	const handleShare = () => {
		if (isMobile) {
			const paramConfigs = queryString.parse(window.location.search);
			const params = {
				mediaDetails: {
					id: mediaType === 'scenario' ? paramConfigs.scenarioId : paramConfigs.mediaId,
					title: mediaName || currentMedia?.mediaName,
				},
				configs: { id: paramConfigs.configId, ...settings },
				paramConfigs,
				accountId: accountId,
				type: mediaType,
				time: undefined,
				embedShareSettings: embedShareSettings,
			};
			navigator.share && navigator.share({ url: getPreviewLink({ ...params }) });
			return;
		}
		dispatch(setOpenSharingModal(true));
	};

	const handleDownloadVideo = () => {
		window.open(rewriteUrl((window as any).GoBrain.Utils.setQueryStringParameter(videoDownloadUrl, 'download', true)));
	};

	const rewriteUrl = (url: string) => {
		var topDomain = -1 < url.indexOf('.dna.qbrick.com') ? '.dna.qbrick.com' : -1 < url.indexOf('.dna.local') ? '.dna.local' : null;
		if (topDomain) {
			var subDomains = url.substring(2 + url.indexOf('//'), url.indexOf(topDomain));
			url = url.replace(subDomains, subDomains.replace(/\./g, '-'));
		}

		return 'file:' !== window.location.protocol ? url.replace(/^http[s]?:/, window.location.protocol) : url;
	};

	const color = settings?.playerStyling?.primaryColor ?? '#126cfc';
	const opacity = Number(settings?.playerStyling?.primaryOpacity ?? 100);

	const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacity / 100 };
	const hoverStyle = isMobile ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };
	const enabledTopControlByPreset = settings.topControls.enabled;

	const isInfoExcludedOnThisHost = (option: any, domainList: any) => {
		let isInfoHidden = false;

		const titleDomainList = (domainList ?? '').split(',');

		if (option && titleDomainList.length !== 0) {
			const url = embedShareSettings?.origin ?? '';
			isInfoHidden = titleDomainList.some((domain: any) => domain && url.includes(domain.trim()));
		}

		return isInfoHidden;
	};

	const mediaTitleHiddenOnThisHost = isInfoExcludedOnThisHost(
		settings.topControls?.showExcludingTitleOnDomains,
		settings.topControls?.excludingTitleOnDomains
	);

	const companyNameHiddenOnThisHost = isInfoExcludedOnThisHost(
		settings.topControls?.showExcludingAccountNameOnDomains,
		settings.topControls?.excludingAccountNameOnDomains
	);

	const solidColor = settings?.topControls?.solidBackgroundTopColor ?? '#00000';
	const solidColorStyle = settings.topControls.showSolidBackgroundTopControl
		? {
				backgroundColor: tinycolor({
					...tinycolor(solidColor).toRgb(),
					a: Number(settings.topControls.solidBackgroundTopControlOpacity ?? 100) / 100,
				}).toRgbString(),
			}
		: undefined;

	const { sharing, downloadMedia, openInNewWindow } = queryParams ?? {};
	const showShareButton = !sharing  ? (enabledTopControlByPreset && settings.topControls?.showSocialSharing) : sharing === "true";
	const showDownloadButton = !downloadMedia  ? (enabledTopControlByPreset && settings.topControls?.showDownloadButton) : downloadMedia === "true";
	const showOpenInNewWindow = !openInNewWindow  ? (enabledTopControlByPreset && settings.topControls?.showOpenInNewWindow) : openInNewWindow === "true";

	useEffect(() => {
		const clearTimeId = setTimeout(() => {
			setOverShowingTime(true);
		}, 3000);

		return () => {
			clearTimeout(clearTimeId);
		};
	}, []);

	return (
		<TopControlsContainer className={joinClassNames(queryParams.inline === 'off' ? 'top-control--not-inline' : '', className)}>
			{playlistId === '' && (
				<TopControlsLeft className="topControlsLeft">
					<TopControlsLeftFirstRow>
						{enabledTopControlByPreset &&
							settings.topControls?.showLogotype &&
							settings.topControls?.logotypeUrl &&
							!companyNameHiddenOnThisHost && (
								<img
									style={{
										opacity: `${isShowingAccName ? 1 : 0}`,
										transition: 'all 0.5s',
									}}
									aria-hidden={true}
									className="topControlsLeft__logo"
									src={settings.topControls?.logotypeUrl}
								/>
							)}
						<TopControlsPlaybackTitleWrapper>
							{enabledTopControlByPreset && settings.topControls?.showTitle && !mediaTitleHiddenOnThisHost && (
								<TopControlsPlaybackTitle
									className="topControlbar__control--title"
									aria-hidden={!isShowingTitle}
									aria-label={`${t('ARIA_LABEL_MEDIA_TITLE')}${mediaName || currentMedia?.mediaName}`}
									style={{
										...solidColorStyle,
										width: 'fit-content',
										borderRadius: '4px',
										padding: '4px',
										opacity: `${isShowingTitle ? 1 : 0}`,
										transition: 'all 0.5s',
									}}
									role="heading"
									aria-level={2}
								>
									<TopControlsMediaTitle aria-hidden="true" isMobile={isMobile}>
										{mediaName || currentMedia?.mediaName}
									</TopControlsMediaTitle>
								</TopControlsPlaybackTitle>
							)}
							{enabledTopControlByPreset && settings.topControls?.showAccountName && accountName && !companyNameHiddenOnThisHost && (
								<TopControlsPlaybackTitle
									className="topControlbar__control--accountName"
									aria-label={`${t('ARIA_LABEL_PUBLISHER')}${accountName}`}
									aria-hidden={!isShowingAccName}
									style={{
										...solidColorStyle,
										width: 'fit-content',
										borderRadius: '4px',
										padding: '4px',
										opacity: `${isShowingAccName ? 1 : 0}`,
										transition: 'all 0.5s',
									}}
									role="heading"
									aria-level={2}
								>
									<TopControlsAccountTitle aria-hidden="true" isMobile={isMobile}>
										{accountName}
									</TopControlsAccountTitle>
								</TopControlsPlaybackTitle>
							)}
						</TopControlsPlaybackTitleWrapper>
					</TopControlsLeftFirstRow>
				</TopControlsLeft>
			)}
			{children}
			<TopControlsRight className="topControlsRight" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
				{!isMediaLive && showDownloadButton && videoDownloadUrl && (
					<ControlButton
						icon={<GetApp htmlColor="#fff" />}
						tabIndex={0}
						ariaLabel={t('DOWNLOAD')}
						tooltip={t('DOWNLOAD_TOOLTIP')}
						style={solidColorStyle}
						hoverStyle={hoverStyle}
						onClick={handleDownloadVideo}
						topControl
					/>
				)}

				{showShareButton && (
					<ControlButton
						icon={<Share htmlColor="#fff" />}
						tabIndex={0}
						ariaLabel={t('SHARE')}
						tooltip={t('SHARE_TOOLTIP')}
						style={solidColorStyle}
						hoverStyle={hoverStyle}
						onClick={handleShare}
						topControl
					/>
				)}

				{showOpenInNewWindow && (
					<ControlButton
						icon={<OpenInNew htmlColor="#fff" />}
						tabIndex={0}
						aria-label={t('OPEN_IN_NEW_WINDOW')}
						tooltip={t('OPEN_IN_NEW_WINDOW')}
						style={solidColorStyle}
						hoverStyle={hoverStyle}
						onClick={handleOpenInNewWindow}
						topControl
					/>
				)}
			</TopControlsRight>
			{enabledTopControlByPreset && settings.topControls?.showHoverShadow && <TopControlsShadow aria-hidden="true" />}
		</TopControlsContainer>
	);
};

export default TopControls;
